import {
  DateRange,
  ID,
  OrderDirection,
  priorityRanking,
  SortingMode,
  Task,
  TaskPriorityOptions,
  Timeslot,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { endOfDay, isAfter } from 'date-fns';

import { getTaskDueDate } from './get-task-due-date';
import { getDueTaskPlanning, getTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';

type TaskSortGeneratorOptions = {
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
};

const taskSortGenerator =
  ({ invertedTimeslots, allDayFirst }: TaskSortGeneratorOptions = {}) =>
  (
    taskA: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
    taskB: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    },
  ) =>
    !!taskA.sortDate && !!taskB.sortDate
      ? taskA.sortDate.getTime() - taskB.sortDate.getTime() === 0
        ? isNumber(taskA.timeslot?.startTime) &&
          isNumber(taskB.timeslot?.startTime)
          ? invertedTimeslots
            ? taskB.timeslot!.startTime - taskA.timeslot!.startTime
            : taskA.timeslot!.startTime - taskB.timeslot!.startTime
          : isNumber(taskA.timeslot?.startTime)
            ? allDayFirst
              ? 1
              : -1
            : isNumber(taskB.timeslot?.startTime)
              ? allDayFirst
                ? -1
                : 1
              : 0
        : taskA.sortDate.getTime() - taskB.sortDate.getTime()
      : taskA.sortDate
        ? -1
        : 1;

export type DueDateOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

const getSortedTasksByDueDate = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    invertedTimeslots,
    allDayFirst,
    expectedDueDate,
    dateRange,
  }: DueDateOptions,
) => {
  const endOfExpectedDate = endOfDay(expectedDueDate ?? today);
  const { deadlineTasks, dueDateTasks } = tasks.reduce<{
    deadlineTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
    dueDateTasks: {
      task: Task;
      sortDate?: Timestamp;
      timeslot?: Timeslot | null;
    }[];
  }>(
    (acc, task) => {
      if (dateRange && task.schedules) {
        const planning = getTaskPlanning(task, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          today,
          weekStartsOn,
          sinceLastEntryOnly: true,
          uncompletedOnly: true,
        });

        const plannedOccurrence = planning[0];
        const dueDate =
          plannedOccurrence?.date ??
          getTaskDueDate(task, { today, weekStartsOn });

        const timeslot = plannedOccurrence
          ? plannedOccurrence.timeslot
          : task.timeslot;

        if (!dueDate) {
          acc.dueDateTasks.push({
            task,
            timeslot,
          });
          return acc;
        }

        acc.dueDateTasks.push({
          task,
          sortDate: endOfDay(dueDate),
          timeslot,
        });
        return acc;
      }

      const planning = getDueTaskPlanning(task, {
        date: endOfExpectedDate,
        today,
        weekStartsOn,
      });
      const duePlanning = planning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, endOfExpectedDate),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const dueDate =
        plannedOccurrence?.date ??
        getTaskDueDate(task, { today, weekStartsOn });

      const timeslot = plannedOccurrence
        ? plannedOccurrence.timeslot
        : task.timeslot;

      if (!dueDate && !task.deadlineDeadline) {
        acc.dueDateTasks.push({
          task,
          timeslot,
        });
        return acc;
      }

      if (dueDate) {
        acc.dueDateTasks.push({
          task,
          sortDate: endOfDay(dueDate),
          timeslot,
        });
        return acc;
      }

      if (task.deadlineDeadline) {
        acc.deadlineTasks.push({
          task,
          sortDate: endOfDay(task.deadlineDeadline),
          timeslot,
        });
      }

      return acc;
    },
    { deadlineTasks: [], dueDateTasks: [] },
  );

  const sortFunc = taskSortGenerator({ invertedTimeslots, allDayFirst });

  return [...deadlineTasks.sort(sortFunc), ...dueDateTasks.sort(sortFunc)].map(
    ({ task }) => task,
  );
};

export type PriorityOptions = {
  direction?: OrderDirection;
};

const getSortedTasksByPriority = (
  tasks: Task[],
  { direction = OrderDirection.Asc }: PriorityOptions,
) =>
  [...tasks].sort((taskA, taskB) => {
    const directionTaskA = direction === OrderDirection.Asc ? taskA : taskB;
    const directionTaskB = direction === OrderDirection.Asc ? taskB : taskA;

    if (
      !directionTaskA.priority ||
      directionTaskA.priority === TaskPriorityOptions.None
    ) {
      return 1;
    }

    if (
      !directionTaskB.priority ||
      directionTaskB.priority === TaskPriorityOptions.None
    ) {
      return -1;
    }

    return (
      priorityRanking[directionTaskA.priority] -
      priorityRanking[directionTaskB.priority]
    );
  });

const getSortedTasksByCustom = (tasks: Task[], order: ID[]) =>
  [...tasks].sort((taskA, taskB) => {
    const indexA = order.indexOf(taskA.id);
    const indexB = order.indexOf(taskB.id);

    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });

export type SortedTasksOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  direction?: OrderDirection;
  mode?: SortingMode[];
  order?: ID[];
  invertedTimeslots?: boolean;
  allDayFirst?: boolean;
  expectedDueDate?: Timestamp;
  dateRange?: DateRange;
};

export const getSortedTasks = (
  tasks: Task[],
  {
    today,
    weekStartsOn,
    direction,
    mode = [],
    order = [],
    invertedTimeslots,
    allDayFirst,
    expectedDueDate,
    dateRange,
  }: SortedTasksOptions,
) => {
  let sortedTasks = [...tasks]
    // first sort on createdAt to have a base ordering in case there are multiple tasks with same order values
    .sort(
      (taskA, taskB) => taskA.createdAt.getTime() - taskB.createdAt.getTime(),
    );

  mode?.forEach((mode) => {
    switch (mode) {
      case SortingMode.DueDate:
        sortedTasks = getSortedTasksByDueDate(sortedTasks, {
          today,
          weekStartsOn,
          invertedTimeslots,
          allDayFirst,
          expectedDueDate,
          dateRange,
        });
        break;
      case SortingMode.DeadlineDeadline:
        sortedTasks = [...sortedTasks].sort((taskA, taskB) => {
          if (!taskA.deadlineDeadline && !taskB.deadlineDeadline) {
            return 0;
          }
          if (!taskA.deadlineDeadline) {
            return 1;
          }
          if (!taskB.deadlineDeadline) {
            return -1;
          }
          return (
            taskA.deadlineDeadline.getTime() - taskB.deadlineDeadline.getTime()
          );
        });
        break;
      case SortingMode.Priority:
        sortedTasks = getSortedTasksByPriority(sortedTasks, { direction });
        break;
      case SortingMode.Name:
        sortedTasks = [...sortedTasks].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        break;
      case SortingMode.DeletedAt:
        sortedTasks = [...sortedTasks].sort((taskA, taskB) => {
          if (!taskA.deletedAt && !taskB.deletedAt) {
            return 0;
          }
          if (!taskA.deletedAt) {
            return 1;
          }
          if (!taskB.deletedAt) {
            return -1;
          }
          return taskB.deletedAt.getTime() - taskA.deletedAt.getTime();
        });
        break;
      case SortingMode.Custom:
        sortedTasks = getSortedTasksByCustom(sortedTasks, order);
        break;
    }
  });

  return sortedTasks;
};
